import { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Webhook, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const outcomeTone = (o) => o === "refreshed" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
  : o === "error" ? "bg-red-100 text-red-800 border-red-200"
  : "bg-slate-100 text-slate-700 border-slate-200";

export default function LoanDiskWebhooksCard() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [s, e] = await Promise.all([api.get("/webhooks/loandisk/status"), api.get("/webhooks/loandisk/events", { params: { limit: 25 } })]);
      setStatus(s.data); setEvents(e.data.events || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not load webhook status"); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const url = status?.endpoint_url || `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/loandisk`;
  const copy = () => {
    if (!navigator.clipboard?.writeText) { toast.error("Clipboard not available — copy the URL manually"); return; }
    navigator.clipboard.writeText(url).then(() => toast.success("Webhook URL copied"), () => toast.error("Couldn't copy — copy the URL manually"));
  };

  return (
    <Card className="mb-4" data-testid="loandisk-webhooks-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Webhook className="w-4 h-4" /> LoanDisk webhooks
          <Badge variant="outline" className={status?.secret_configured ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"} data-testid="loandisk-webhooks-secret-badge">
            {status ? (status.secret_configured ? "Signature verified" : "Capture mode — no signing secret yet") : "…"}
          </Badge>
          <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={load} disabled={busy} data-testid="loandisk-webhooks-refresh">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </CardTitle>
        <CardDescription className="text-xs">
          Paste this URL into LoanDisk → Admin → API Settings → Webhooks (repayment + loan status events). Each delivery triggers a fresh read of that loan from LoanDisk and refreshes the collections file — the payload itself is never trusted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-slate-100 px-2 py-1 font-mono text-[11px]" data-testid="loandisk-webhooks-url">{url}</code>
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={copy} data-testid="loandisk-webhooks-copy"><Copy className="w-3.5 h-3.5" /></Button>
        </div>
        {status && (
          <div className="flex flex-wrap gap-4 text-slate-600" data-testid="loandisk-webhooks-stats">
            <span><b>{status.total}</b> received</span>
            <span><b>{status.refreshed}</b> files refreshed</span>
            <span><b>{status.rejected}</b> rejected (bad signature)</span>
            <span>Last: {status.last?.received_at ? new Date(status.last.received_at).toLocaleString("en-GB") : "never"}</span>
          </div>
        )}
        {events.length > 0 && (
          <div className="max-h-56 overflow-y-auto border rounded" data-testid="loandisk-webhooks-events">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left px-2 py-1">Received</th><th className="text-left px-2 py-1">Event</th><th className="text-left px-2 py-1">Loan</th><th className="text-left px-2 py-1">Source</th><th className="text-left px-2 py-1">Outcome</th></tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.event_id} className="border-t" data-testid="loandisk-webhook-event-row">
                    <td className="px-2 py-1 whitespace-nowrap">{new Date(e.received_at).toLocaleString("en-GB")}</td>
                    <td className="px-2 py-1">{e.event_type}{e.object ? ` · ${e.object}` : ""}{!e.signature_ok && <span className="ml-1 text-amber-700">(unverified)</span>}</td>
                    <td className="px-2 py-1 font-mono">{e.loan_id || "—"}{e.region ? ` ${e.region}` : ""}</td>
                    <td className="px-2 py-1">{e.source || "—"}</td>
                    <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded border ${outcomeTone(e.result?.outcome)}`}>{e.rejected ? "rejected" : e.result?.outcome || (e.processed ? "done" : "pending")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
